import type {Ref} from 'react';
import {VNode} from 'snabbdom';

import {KeyModifiers} from 'common/util/keyboard';

export enum Theme {
  Default = `default`,
  GlobalSidenav = `global-sidenav`,
}

export enum PanelVisibility {
  // `auto` makes resizable-panel behave like a controlled component, with expand/collapse state persisted.
  Auto = `auto`,
  Collapsed = `collapsed`,
  Expanded = `expanded`,
}

export enum Direction {
  Right = `right`,
  Left = `left`,
  Top = `top`,
  Bottom = `bottom`,
}

export enum PanelName {
  Events = `Events Menu Sidebar`,
  DataTable = `Data Table`,
  Properties = `Properties Menu Sidebar`,
  QueryPanel = `Query Panel`,
  SideNav = `Side Nav`,
  MultiMetrics = `Multi Metrics Menu Sidebar`,
  ConversationSidePanel = `Conversation Side Panel`,
}

export enum ResizeToCollapseMode {
  Default = `default`,
  Always = `always`,
}

export enum CollapseButtonTheme {
  Stretched = `stretched`,
  Simple = `simple`,
  None = `none`,
}

export interface ResizablePanelAttrs {
  'collapse-key'?: string;
  'collapse-key-modifier'?: KeyModifiers;
  'default-alignment'?: Direction;
  'default-ratio'?: number;
  'default-size'?: number;
  'disable-collapsing'?: boolean;
  'disable-responsive-resize'?: boolean;
  'fixed-size'?: boolean;
  'ignore-persisted-collapse'?: boolean;
  'max-auto-size'?: number;
  'max-size'?: number;
  'min-adjacent-size'?: number;
  'min-size'?: number;
  'panel-name'?: PanelName;
  'resize-to-collapse-mode'?: ResizeToCollapseMode;
  'show-alignment-toggle'?: boolean;
  'collapse-button-theme'?: CollapseButtonTheme;
  'storage-key'?: string;
  theme?: Theme;
  visibility?: PanelVisibility;
}

export interface ResizablePanelState {
  isHoveringAlignmentToggle: boolean;
  hasResized: boolean;
  alignment?: Direction;
  isCollapsed?: boolean;
  isResizing: boolean;
  size: number;
  currentRatio?: number;
  containerWidth: number;
  containerHeight: number;
  snapResetState?: boolean;
}

export type PersistedResizablePanelState = Pick<ResizablePanelState, `alignment` | `isCollapsed` | `currentRatio`> & {
  /** null until the user resizes the panel */
  size: number | null;
};

export enum TrackingEvent {
  CollapsedPanel = `Collapsed Panel`,
  ExpandedPanel = `Expanded Panel`,
  ToggleAlignment = `Toggle Alignment`,
  Resize = `Resize`,
  Reset = `Reset Panel`,
}

export enum ResetPanelSource {
  DoubleClick = `Double Click`,
  SnapToDefault = `Snap to Default`,
}

export enum ExpandPanelSource {
  SwitchToFixedSize = `Switch to Fixed Size`,
}

export enum CollapsePanelSource {
  ResizedToEdge = `Resized to Edge`,
}

export enum OtherTogglePanelSource {
  Button = `Button`,
  KeyboardShortcut = `Keyboard Shortcut`,
  CommandPalette = `Command Palette`,
}

export type TogglePanelSource = OtherTogglePanelSource | ExpandPanelSource | CollapsePanelSource;

/** Imperative controls a `ResizablePanel` exposes through its `actionsRef` prop. */
export interface ResizablePanelActions {
  toggleCollapsed: (options: {source: TogglePanelSource; keyboardShortcut?: string}) => void;
}

export enum ToggleAlignmentSource {
  Button = `Button`,
}

export interface ResizablePanelJsxProps {
  attrs?: ResizablePanelAttrs;
  on?: {
    panelResize?: (
      ev: CustomEvent<{size: number; isCollapsed: boolean; togglePanelSource?: TogglePanelSource}>,
    ) => void;
    updateAlignment?: (ev: CustomEvent<{newAlignment: Direction}>) => void;
  };
  props?: {
    headerContent?: VNode;
    actionsRef?: Ref<ResizablePanelActions>;
  };
}
